// What a caster input's form saves of its caster settings.
import type { Input } from './api'

type Caster = NonNullable<Input['caster']>

// casterBody is the caster object an input's form saves: the stored one
// with the fields the form owns put over it. Comms and talkback are set
// from the detail panel, not the form: rebuilding the object from the
// form's fields alone dropped them on every save, a rename included
// (WEB-26), and the caster lost production's talk mid-show.
export function casterBody(c: Caster | undefined): Caster | undefined {
    if (!c || !Object.values(c).some((v) => v)) return undefined
    if (c.players) {
        // players get nothing back: no return feed, comms or talkback (the
        // node refuses them on a player input)
        return {
            players: true,
            route: c.route || undefined,
            bitrate_k: Number(c.bitrate_k) || undefined,
            latency: c.latency || undefined,
        }
    }
    const ret = !!c.return_input || !!c.return_from
    return {
        ...c,
        route: c.route || undefined,
        bitrate_k: Number(c.bitrate_k) || undefined,
        return_input: c.return_input || undefined,
        return_from: c.return_from || undefined,
        return_height: ret ? Number(c.return_height) || undefined : undefined,
        return_bitrate_k: ret ? Number(c.return_bitrate_k) || undefined : undefined,
        return_fps: ret ? c.return_fps || undefined : undefined,
        return_encoder: ret ? c.return_encoder || undefined : undefined,
        return_pair: ret ? Number(c.return_pair) || undefined : undefined,
        latency: c.latency || undefined,
    }
}

// The return feed's choice as one select value: "caster:<id>" takes that
// caster input's return feed (return_from: the node copies its settings
// in, one encode for both), an input id the input itself, "" none.
export function returnChoice(c: Caster | undefined): string {
    if (c?.return_from) return 'caster:' + c.return_from
    return c?.return_input ?? ''
}

// withReturnChoice is c with the return feed v (returnChoice's values).
// Following another caster drops this one's own picture settings: the
// node puts the leader's in. The pair stays this caster's.
export function withReturnChoice(c: Caster | undefined, v: string): Caster {
    if (v.startsWith('caster:')) {
        return {
            ...c,
            return_from: v.slice('caster:'.length),
            return_input: undefined,
            return_height: undefined,
            return_bitrate_k: undefined,
            return_fps: undefined,
            return_encoder: undefined,
        }
    }
    return { ...c, return_from: undefined, return_input: v || undefined }
}

// returnLeaders are the caster inputs other than self with a return feed
// another caster can take; the first is a new caster input's default.
export function returnLeaders(inputs: Input[], self: string): Input[] {
    return inputs.filter((i) => i.id !== self && i.kind === 'caster' && !!i.caster?.return_input)
}
